import { HttpClient, HttpErrorResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import { NgIdleModule } from '@ng-idle/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BASE_CONFIG_TOKEN } from '@sg/api/base-api-lib';
import { PblNformMaterialModule } from '@pebula/nform-material';
import {
  EnvironmentService,
  EnvironmentServiceBase,
  GlobalRouteGuard,
  SgAsyncGuiDialogBoxComponent,
  SgAsyncGuiToasterPluginModule,
  SgCachingService,
  SgCommonModule,
  SgCommonFormsModule,
  SgDynamicHttpInterceptionModule,
  SG_GLOBAL_ERROR_HANDLER_PROVIDER,
  SgLogglyTracker,
  SgModulePreloadingStrategy,
  SgNavMenuService,
  SgPoller,
  SgSidenavService,
  SgToasterService,
  SgTopNavService,
  SgTranslationService,
  SgYesNoClickConfirmPanel,
  UserSessionAuthAdapterToken,
  UserSessionService,
} from '@sg/common';
import { SG_ASYNC_GUI_PLUGIN, SgAsyncGuiDialogPluginModule, SgAsyncGuiModule } from '@sg/ui-kit/async-gui';
import {
  SG_CLICK_CONFIRM_PLUGIN,
  SgClickConfirmDialogPluginModule,
  SgClickConfirmModule,
  SgClickConfirmPopoverPluginModule,
} from '@sg/ui-kit/click-confirm';
import { DateTime } from 'luxon';
import { provideLottieOptions } from 'ngx-lottie';
import { currentApp, getAuthAdapter, getDeployUrl } from '../app-bootstrap';
import { envApi } from '../environments/environment';
import { AppComponent } from './app.component';
import { ROUTES, protectUnauthorizedRoutes } from './app.routes';
import { SgBasicAppInitAndTemplatesComponent } from './basic-app-init-and-templates/basic-app-init-and-templates.component';
import { SgBuildWatermarkComponent } from './build-watermark/build-watermark.component';

export function HttpLoaderFactory(http: HttpClient, env: EnvironmentService) {
  const version = env.meta?.version ?? DateTime.now().toFormat('yy_M_d_Hm');
  return new TranslateHttpLoader(http, `${getDeployUrl('/')}assets/i18n/`, `.json?v=${version}`);
}

export const APP_ASSETS_PATH = 'APP_ASSETS_PATH';
function appAssetsPathFactory(env) {
  return `${env.getDeployUrl('/')}assets/`;
}

const environmentServiceBase = {
  env: envApi.environment,
  get appName() {
    return currentApp?.name;
  },
  get branding() {
    return currentApp.branding;
  },
  get deployUrl() {
    return getDeployUrl();
  },
};

@NgModule({
  declarations: [AppComponent, SgBasicAppInitAndTemplatesComponent, SgBuildWatermarkComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    SgDynamicHttpInterceptionModule.forRoot(),
    RouterModule.forRoot(ROUTES, {
      // enableTracing: true,
      useHash: false,
      preloadingStrategy: SgModulePreloadingStrategy,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, EnvironmentService],
      },
    }),
    NgIdleModule.forRoot(),
    PblNformMaterialModule,
    SgCommonModule.forRoot(),
    SgCommonFormsModule.forRoot(),
    SgAsyncGuiModule,
    SgAsyncGuiDialogPluginModule,
    SgClickConfirmModule,
    SgClickConfirmDialogPluginModule,
    SgClickConfirmPopoverPluginModule,
  ],
  providers: [
    { provide: EnvironmentServiceBase, useValue: environmentServiceBase },
    EnvironmentService,
    SgCachingService,
    SgLogglyTracker,
    SG_GLOBAL_ERROR_HANDLER_PROVIDER,
    SgToasterService,
    SgTranslationService,
    GlobalRouteGuard,
    UserSessionService,
    SgNavMenuService,
    SgTopNavService,
    SgSidenavService,
    { provide: UserSessionAuthAdapterToken, useValue: getAuthAdapter },
    { provide: BASE_CONFIG_TOKEN, useFactory: () => envApi.environment.api },
    SgModulePreloadingStrategy,
    SgPoller,
    ...PblNformMaterialModule.forRoot().providers,
    ...SgClickConfirmModule.forRoot({ style: 'dialog', component: SgYesNoClickConfirmPanel }).providers,
    { provide: SG_CLICK_CONFIRM_PLUGIN, useClass: SgClickConfirmDialogPluginModule.PLUGIN, multi: true },
    { provide: SG_CLICK_CONFIRM_PLUGIN, useClass: SgClickConfirmPopoverPluginModule.PLUGIN, multi: true },
    ...SgAsyncGuiModule.forRoot({ style: 'dialog', component: SgAsyncGuiDialogBoxComponent }).providers,
    { provide: SG_ASYNC_GUI_PLUGIN, useClass: SgAsyncGuiDialogPluginModule.PLUGIN, multi: true },
    { provide: SG_ASYNC_GUI_PLUGIN, useClass: SgAsyncGuiToasterPluginModule.PLUGIN, multi: true },
    { provide: APP_ASSETS_PATH, useFactory: appAssetsPathFactory, deps: [EnvironmentService] },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        enterAnimationDuration: '0ms',
        exitAnimationDuration: '0ms',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
      },
    },
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
  ],
})
export class AppModule {
  enableSideNav = true;

  constructor(
    router: Router,
    sgTranslation: SgTranslationService,
    errorHandler: ErrorHandler,
    logger: SgLogglyTracker
  ) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        if (event.error instanceof HttpErrorResponse) {
          // A failed resolver/guard HTTP call. SgGlobalErrorHandler skips HttpErrorResponse (it relies on
          // SgHttpLoggingInterceptor, which not every app registers and which skips 404s), so log it directly.
          logger.error(event.error);
        } else {
          // Route through ErrorHandler (not the tracker directly): the Router ALSO reports a failed routerLink/popstate
          // navigation to ErrorHandler, and SgGlobalErrorHandler's per-signature throttle collapses the two into one
          // log. Trade-off: a user retrying the same failing navigation within 60s is also logged only once.
          errorHandler.handleError(event.error);
        }
      }
    });

    // TODO: Currently, APP_MATCH.current is based on url (parsing).
    //       In the future we might want this to be service based, role based sr subdomain based.
    //       The entire process should be handled in APP_MATCH.current and no change should be done here.
    //       If it's async (service/role) then do the async operation before angular bootstraps - APP_MATCH.current must
    //       remain sync.
    const app = currentApp;

    // remove lazy loaded app's that are not related to the current context so they will not load.
    // currently we always return 1 app, we return array of apps for multi-app support in the future.
    const newConfig = protectUnauthorizedRoutes([app.name]);
    router.resetConfig(newConfig);

    sgTranslation.init();

    router.events.subscribe((event) => {
      if ((window as any).ga) {
        if (event instanceof NavigationEnd) {
          (window as any).ga('set', 'page', event.urlAfterRedirects);
          (window as any).ga('send', 'pageview');
        }
      }
    });
  }
}
